import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import { Button } from '@/src/shadComponents/ui/button';
import { Loader2 } from 'lucide-react';
import {
    Download,
    Upload,
    EyeOff,
    EyeIcon,
    FilterIcon,
    LucideGalleryThumbnails,
    SendIcon,
    Settings2,
    Tablet,
} from 'lucide-react';
import { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { ActivationDevice } from './hooks/useActivationDevices';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuCheckboxItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
} from '@/src/shadComponents/ui/dropdown-menu';

const filterOptions = [
    { name: 'Photo', value: 'processed' },
    { name: 'Video', value: 'processed-video' },
    { name: 'Edited', value: 'user-edit' },
    { name: 'Prints', value: 'composite' },
    { name: 'Originals', value: 'original' },
];

export default function PhotoManagerFilters({
    photos,
    setPhotos,
    onFilterChange,
    activationId,
    setIsShowingZipPrompt,
    setIsShowingBulkUpload,
    showHidden,
    setShowHidden,
    sortOrder,
    setSortOrder,
    selectedShareIdentifiers,
    setSelectedShareIdentifiers,
    columnCount,
    setColumnCount,
    setIsShowingSharePrompt,
    deviceFilter,
    setDeviceFilter,
    activationDevices,
    allPhotos,
    autoRefresh,
    setAutoRefresh,
}: {
    photos: MediaInstance[];
    onFilterChange: (filter: string[]) => void;
    onSortChange: (sort: 'asc' | 'desc') => void;
    activationId: number;
    isShowingZipPrompt: boolean;
    setIsShowingZipPrompt: (isShowing: boolean) => void;
    isShowingBulkUpload: boolean;
    setIsShowingBulkUpload: (isShowing: boolean) => void;
    showHidden: boolean;
    setShowHidden: (showHidden: boolean) => void;
    sortOrder: 'asc' | 'desc';
    setSortOrder: (sort: 'asc' | 'desc') => void;
    selectedShareIdentifiers: string[];
    setSelectedShareIdentifiers: (shareIdentifiers: string[]) => void;
    setPhotos: (photos: MediaInstance[]) => void;
    columnCount: number;
    setColumnCount: (columnCount: number) => void;
    setIsShowingSharePrompt: (isShowing: boolean) => void;
    deviceFilter: string | null;
    setDeviceFilter: (deviceFilter: string | null) => void;
    activationDevices: ActivationDevice[];
    allPhotos: MediaInstance[];
    autoRefresh: boolean;
    setAutoRefresh: (autoRefresh: boolean) => void;
}) {
    const [typeFilter, setTypeFilter] = useState<string[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isDeviceFilterOpen, setIsDeviceFilterOpen] = useState(false);
    const authenticatedApi = useAuthenticatedApi();
    const [isReprocessing, setIsReprocessing] = useState(false);

    // Calculate photo counts per device for the filter dropdown
    const devicePhotoCounts = useMemo(() => {
        const counts: Record<string, number> = { uploaded: 0 };
        
        for (const photo of allPhotos) {
            if (!photo.deviceKeyId) {
                counts.uploaded++;
            } else {
                counts[photo.deviceKeyId] = (counts[photo.deviceKeyId] || 0) + 1;
            }
        }
        
        return counts;
    }, [allPhotos]);

    // Get display name for a device
    const getDeviceDisplayName = (deviceKeyId: string): string => {
        const device = activationDevices.find(d => d.deviceKeyId === deviceKeyId);
        return device?.displayName || device?.deviceName || 'Unknown Device';
    };

    // Get the current filter label
    const getDeviceFilterLabel = (): string => {
        if (deviceFilter === null) return 'All Sources';
        if (deviceFilter === 'uploaded') return 'Uploaded';
        return getDeviceDisplayName(deviceFilter);
    };

    useEffect(() => {
        if (typeFilter.length === 0) {
            setTypeFilter([
                'processed',
                'processed-video',
                'user-edit',
                'composite',
                'original',
            ]);
        }
    }, [setTypeFilter, typeFilter]);

    useEffect(() => {
        // When all types are selected, pass empty array to indicate no filter
        onFilterChange(typeFilter.length === 5 ? [] : typeFilter);

        // eslint-disable-next-line
    }, [typeFilter]);

    const [isHidden, setIsHidden] = useState(false);

    return (
        <div className="px-4 sm:px-6 lg:px-8 pb-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                {/* Left side: Title and Filters */}
                <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="text-2xl font-bold tracking-tight text-foreground">
                        Photos ({photos.length})
                    </h1>
                    
                    {/* Type Filter */}
                    <div className="relative">
                        <button
                            type="button"
                            className="bg-background border border-border flex items-center rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 hover:bg-muted/50"
                            onClick={() => setIsOpen(!isOpen)}
                        >
                            <FilterIcon className="h-4 w-4 mr-2 text-muted-foreground" />
                            {typeFilter.length === 5
                                ? 'All Types'
                                : `${typeFilter.length} Types`}
                        </button>

                        {isOpen && (
                            <div className="absolute mt-1 w-48 bg-background border border-border rounded-md shadow-lg z-20">
                                <div className="p-1">
                                    <div
                                        className="cursor-pointer hover:bg-muted px-3 py-2 rounded text-sm"
                                        onClick={() => {
                                            setTypeFilter([
                                                'processed',
                                                'processed-video',
                                                'user-edit',
                                                'composite',
                                                'original',
                                            ]);
                                            setIsOpen(false);
                                        }}
                                    >
                                        All Types
                                    </div>
                                    <div className="border-t border-border my-1" />
                                    {filterOptions.map((option) => (
                                        <div
                                            key={option.value}
                                            className="cursor-pointer hover:bg-muted px-3 py-1.5 rounded flex items-center text-sm"
                                            onClick={() => {
                                                setTypeFilter((prev) =>
                                                    prev.includes(option.value)
                                                        ? prev.filter((v) => v !== option.value)
                                                        : [...prev, option.value],
                                                );
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={typeFilter.includes(option.value)}
                                                onChange={() => {}}
                                                className="mr-2 h-4 w-4 rounded border-border"
                                            />
                                            {option.name}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Source Filter */}
                    <div className="relative">
                        <button
                            type="button"
                            className="bg-background border border-border flex items-center rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 hover:bg-muted/50"
                            onClick={() => setIsDeviceFilterOpen(!isDeviceFilterOpen)}
                        >
                            <Tablet className="h-4 w-4 mr-2 text-muted-foreground" />
                            {getDeviceFilterLabel()}
                        </button>

                        {isDeviceFilterOpen && (
                            <div className="absolute mt-1 w-56 bg-background border border-border rounded-md shadow-lg z-20">
                                <div className="p-1">
                                    <div
                                        className={`cursor-pointer hover:bg-muted px-3 py-2 rounded flex items-center justify-between text-sm ${
                                            deviceFilter === null ? 'bg-blue-50' : ''
                                        }`}
                                        onClick={() => {
                                            setDeviceFilter(null);
                                            setIsDeviceFilterOpen(false);
                                        }}
                                    >
                                        <span>All Sources</span>
                                        <span className="text-xs text-muted-foreground">{allPhotos.length}</span>
                                    </div>
                                    <div className="border-t border-border my-1" />
                                    {devicePhotoCounts.uploaded > 0 && (
                                        <div
                                            className={`cursor-pointer hover:bg-muted px-3 py-2 rounded flex items-center justify-between text-sm ${
                                                deviceFilter === 'uploaded' ? 'bg-blue-50' : ''
                                            }`}
                                            onClick={() => {
                                                setDeviceFilter('uploaded');
                                                setIsDeviceFilterOpen(false);
                                            }}
                                        >
                                            <span className="text-muted-foreground italic">Uploaded</span>
                                            <span className="text-xs text-muted-foreground">{devicePhotoCounts.uploaded}</span>
                                        </div>
                                    )}
                                    {activationDevices.map((device) => {
                                        const count = devicePhotoCounts[device.deviceKeyId] || 0;
                                        if (count === 0) return null;
                                        return (
                                            <div
                                                key={device.deviceKeyId}
                                                className={`cursor-pointer hover:bg-muted px-3 py-2 rounded flex items-center justify-between text-sm ${
                                                    deviceFilter === device.deviceKeyId ? 'bg-blue-50' : ''
                                                }`}
                                                onClick={() => {
                                                    setDeviceFilter(device.deviceKeyId);
                                                    setIsDeviceFilterOpen(false);
                                                }}
                                            >
                                                <div className="flex flex-col">
                                                    <span className={device.friendlyName ? 'font-medium' : ''}>
                                                        {device.displayName}
                                                    </span>
                                                    {device.friendlyName && device.deviceName && (
                                                        <span className="text-xs text-muted-foreground">{device.deviceName}</span>
                                                    )}
                                                </div>
                                                <span className="text-xs text-muted-foreground">{count}</span>
                                            </div>
                                        );
                                    })}
                                    {Object.entries(devicePhotoCounts)
                                        .filter(([key]) => key !== 'uploaded' && !activationDevices.some(d => d.deviceKeyId === key))
                                        .map(([deviceKeyId, count]) => (
                                            <div
                                                key={deviceKeyId}
                                                className={`cursor-pointer hover:bg-muted px-3 py-2 rounded flex items-center justify-between text-sm ${
                                                    deviceFilter === deviceKeyId ? 'bg-blue-50' : ''
                                                }`}
                                                onClick={() => {
                                                    setDeviceFilter(deviceKeyId);
                                                    setIsDeviceFilterOpen(false);
                                                }}
                                            >
                                                <span className="text-muted-foreground">Unknown Device</span>
                                                <span className="text-xs text-muted-foreground">{count}</span>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                        )}
                    </div>

                    {/* View Settings Dropdown */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button className="bg-background border border-border flex items-center rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 hover:bg-muted/50">
                                <Settings2 className="h-4 w-4 mr-2 text-muted-foreground" />
                                View
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-48">
                            <DropdownMenuLabel>Sort Order</DropdownMenuLabel>
                            <DropdownMenuRadioGroup value={sortOrder} onValueChange={(v) => setSortOrder(v as 'asc' | 'desc')}>
                                <DropdownMenuRadioItem value="desc">Newest First</DropdownMenuRadioItem>
                                <DropdownMenuRadioItem value="asc">Oldest First</DropdownMenuRadioItem>
                            </DropdownMenuRadioGroup>
                            
                            <DropdownMenuSeparator />
                            
                            <DropdownMenuLabel>Columns</DropdownMenuLabel>
                            <DropdownMenuRadioGroup value={columnCount.toString()} onValueChange={(v) => setColumnCount(parseInt(v))}>
                                {[2, 3, 4, 5, 6].map((num) => (
                                    <DropdownMenuRadioItem key={num} value={num.toString()}>
                                        {num} Columns
                                    </DropdownMenuRadioItem>
                                ))}
                            </DropdownMenuRadioGroup>
                            
                            <DropdownMenuSeparator />
                            
                            <DropdownMenuCheckboxItem
                                checked={showHidden}
                                onCheckedChange={setShowHidden}
                            >
                                Show Hidden Photos
                            </DropdownMenuCheckboxItem>
                            <DropdownMenuCheckboxItem
                                checked={autoRefresh}
                                onCheckedChange={setAutoRefresh}
                            >
                                Auto Refresh
                            </DropdownMenuCheckboxItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                {/* Right side: Actions */}
                <div className="flex items-center gap-1">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => activationId && setIsShowingSharePrompt(true)}
                        title="Share"
                    >
                        <SendIcon className="h-5 w-5" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={async () => {
                            if (!activationId) return;
                            setIsReprocessing(true);
                            const result = await authenticatedApi.post(`/activations/${activationId}/reprocessThumbnails`);
                            toast.success(`Reprocessed ${result.data.updated} photos of ${result.data.count}.`);
                            setIsReprocessing(false);
                        }}
                        title="Reprocess Thumbnails"
                    >
                        {isReprocessing ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                            <LucideGalleryThumbnails className="h-5 w-5" />
                        )}
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => activationId && setIsShowingZipPrompt(true)}
                        title="Download"
                    >
                        <Download className="h-5 w-5" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsShowingBulkUpload(true)}
                        title="Upload"
                    >
                        <Upload className="h-5 w-5" />
                    </Button>
                </div>
            </div>

            {/* Selection toolbar - fixed at bottom */}
            {selectedShareIdentifiers.length > 0 && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-background rounded-lg px-4 py-2 border border-border shadow-lg z-50">
                    <span className="text-sm font-medium text-foreground">
                        {selectedShareIdentifiers.length} selected
                    </span>
                    <div className="h-4 w-px bg-muted" />
                    <button
                        className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
                        onClick={async () => {
                            const newHiddenState = !isHidden;
                            await authenticatedApi.put(`/media/share-identifiers/visibility`, {
                                shareIdentifiers: selectedShareIdentifiers,
                                isHidden: newHiddenState,
                            });
                            const newPhotos = photos.map((photo) => {
                                if (selectedShareIdentifiers.includes(photo.shareIdentifier)) {
                                    photo.isHidden = newHiddenState;
                                }
                                return photo;
                            });
                            setPhotos(newPhotos);
                            setIsHidden(newHiddenState);
                            toast.success(newHiddenState ? 'Photos hidden' : 'Photos visible');
                        }}
                        title={isHidden ? 'Show Photos' : 'Hide Photos'}
                    >
                        {isHidden ? <EyeIcon className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                    </button>
                    <button
                        className="text-sm text-muted-foreground hover:text-foreground"
                        onClick={() => setSelectedShareIdentifiers([])}
                    >
                        Clear
                    </button>
                </div>
            )}
        </div>
    );
}
